import React, { Suspense, lazy, useEffect } from 'react';
import { ShopProvider, useShop } from './context/ShopContext';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { BasketDrawer } from './components/BasketDrawer';
import { ProductDetailModal } from './components/ProductDetailModal';
import { ToastContainer } from './components/Toast';
import { HomePage } from './pages/HomePage';
import { motion, AnimatePresence } from 'motion/react';
import { Loader2 } from 'lucide-react';
import { perfDiagnostics } from './services/perfDiagnostics';
import { MaintenancePage } from './components/MaintenancePage';
import { auth } from './services/firebase';


// Lazy load secondary routes & developer tools
const ShopPage = lazy(() => import('./pages/ShopPage').then(m => ({ default: m.ShopPage })));
const DonatePage = lazy(() => import('./pages/DonatePage').then(m => ({ default: m.DonatePage })));
const HowItWorksPage = lazy(() => import('./pages/HowItWorksPage').then(m => ({ default: m.HowItWorksPage })));
const AboutPage = lazy(() => import('./pages/AboutPage').then(m => ({ default: m.AboutPage })));
const FAQPage = lazy(() => import('./pages/FAQPage').then(m => ({ default: m.FAQPage })));
const ContactPage = lazy(() => import('./pages/ContactPage').then(m => ({ default: m.ContactPage })));
const NewsPage = lazy(() => import('./pages/NewsPage').then(m => ({ default: m.NewsPage })));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const ReservationConfirmationPage = lazy(() => import('./pages/ReservationConfirmationPage').then(m => ({ default: m.ReservationConfirmationPage })));
const ManageReservationPortal = lazy(() => import('./components/ManageReservationPortal').then(m => ({ default: m.ManageReservationPortal })));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage').then(m => ({ default: m.PrivacyPage })));
const TermsPage = lazy(() => import('./pages/TermsPage').then(m => ({ default: m.TermsPage })));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const CustomerReplyPage = lazy(() => import('./pages/CustomerReplyPage').then(m => ({ default: m.CustomerReplyPage })));
const PerformancePanel = lazy(() => import('./components/PerformancePanel').then(m => ({ default: m.PerformancePanel })));

// Reusable standard page loading indicator
const PageLoader: React.FC = () => (
  <div className="flex flex-col items-center justify-center min-h-[50vh] py-16 text-slate-500">
    <Loader2 className="w-8 h-8 animate-spin text-[#002366] mb-3" />
    <span className="text-sm font-medium text-slate-600">Loading page...</span>
  </div>
);

const MainContent: React.FC = () => {
  const { activePage, setActivePage, isAdminLoggedIn, isAdministrator, settings, isLoadingProfile } = useShop();
  const showPerformanceDiagnostics = isAdministrator && Boolean(settings?.enablePerformanceDiagnostics);
  const [authReady, setAuthReady] = React.useState<boolean>(false);

  useEffect(() => {
    perfDiagnostics.mark('REACT_MOUNTED');
  }, []);

  useEffect(() => {
    if (typeof (auth as any)?.authStateReady === 'function') {
      auth.authStateReady().then(() => setAuthReady(true));
    } else {
      setAuthReady(true);
    }
  }, []);

  const renderPage = () => {
    switch (activePage) {
      case 'home':
        return <HomePage />;
      case 'shop':
        return <ShopPage />;
      case 'donate':
        return <DonatePage />;
      case 'how-it-works':
        return <HowItWorksPage />;
      case 'about':
        return <AboutPage />;
      case 'faq':
        return <FAQPage />;
      case 'contact':
        return <ContactPage />;
      case 'news':
        return <NewsPage />;
      case 'admin':
        return <AdminDashboard />;
      case 'confirmation':
        return <ReservationConfirmationPage />;
      case 'manage-reservation':
        return <ManageReservationPortal />;
      case 'privacy':
        return <PrivacyPage />;
      case 'terms':
        return <TermsPage />;
      case 'reset-password':
        return <ResetPasswordPage />;
      case 'reply':
        return <CustomerReplyPage />;
      default:
        return <HomePage />;
    }
  };

  // 1. If volunteer is logged in and on Admin page, render dedicated Admin management interface without public header/footer
  if (activePage === 'admin' && isAdminLoggedIn) {
    return (
      <div className="min-h-screen bg-slate-50 text-slate-800 font-sans">
        <Suspense fallback={<PageLoader />}>
          <AdminDashboard />
        </Suspense>
        <ToastContainer />
        {showPerformanceDiagnostics && (
          <Suspense fallback={null}>
            <PerformancePanel />
          </Suspense>
        )}
      </div>
    );
  }

  // 2. Central Maintenance Gate: When maintenance mode is ON and visitor is unauthenticated
  if (settings?.maintenanceMode) {
    // Hold briefly with PageLoader during initial session resolution to prevent flash for logged-in staff
    if (!authReady || isLoadingProfile) {
      return <PageLoader />;
    }

    if (!isAdminLoggedIn) {
      // Unauthenticated staff accessing /admin can access the existing admin login page
      if (activePage === 'admin') {
        return (
          <div className="min-h-screen bg-slate-50 text-slate-800 font-sans">
            <Suspense fallback={<PageLoader />}>
              <AdminDashboard />
            </Suspense>
            <ToastContainer />
          </div>
        );
      }

      // Existing staff accessing /reset-password can reset their password
      if (activePage === 'reset-password') {
        return (
          <div className="min-h-screen bg-slate-50 text-slate-800 font-sans">
            <Suspense fallback={<PageLoader />}>
              <ResetPasswordPage />
            </Suspense>
            <ToastContainer />
          </div>
        );
      }

      // All other public routes and bookmarks display ONLY the Maintenance Page
      return (
        <MaintenancePage onStaffLogin={() => setActivePage('admin')} />
      );
    }
  }

  // 3. Otherwise: render the existing application normally

  return (
    <div className="min-h-screen flex flex-col bg-[#f8fafc] text-slate-800 relative overflow-hidden font-sans">
      {/* Background Mesh Gradients for Frosted Glass Theme */}
      <div className="fixed top-[-10%] left-[-10%] w-[55%] h-[55%] rounded-full bg-blue-100/60 blur-[130px] pointer-events-none z-0 print:hidden" />
      <div className="fixed bottom-[-10%] right-[-10%] w-[55%] h-[55%] rounded-full bg-sky-100/60 blur-[130px] pointer-events-none z-0 print:hidden" />
      <div className="fixed top-[40%] right-[15%] w-[35%] h-[35%] rounded-full bg-indigo-100/40 blur-[120px] pointer-events-none z-0 print:hidden" />

      <div className="relative z-10 flex flex-col min-h-screen">
        <Header />
        
        <main className="flex-1 pt-6 sm:pt-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={activePage}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.25, ease: 'easeInOut' }}
            >
              <Suspense fallback={<PageLoader />}>
                {renderPage()}
              </Suspense>
            </motion.div>
          </AnimatePresence>
        </main>

        <Footer />
        <BasketDrawer />
        <ProductDetailModal />
        <ToastContainer />
        {showPerformanceDiagnostics && (
          <Suspense fallback={null}>
            <PerformancePanel />
          </Suspense>
        )}
      </div>
    </div>
  );
};

export default function App() {
  return (
    <ShopProvider>
      <MainContent />
    </ShopProvider>
  );
}
